import { CustomsClassificationSelect } from "@/components/data-driven-inputs/selects/customs-classification/CustomsClassificationSelect";
import { NumericAgrVehicleWeightTextInput } from "@/components/numeric-agr-vehicle-weight-text-input/NumericAgrVehicleWeightTextInput";
import { FormModelChangeHandler } from "@/types/forms/types";
import { GapValues } from "@/lib/utils/GapValues";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import { SpacingValues } from "@/lib/utils/SpacingValues";
import { PriceCalculatorFormData } from "@/features/price-calculator/types/types";
import { Box, Heading, Row, Space } from "@stenajs-webui/core";
import { CheckboxWithLabel, Switch } from "@stenajs-webui/forms";
import * as React from "react";
import { LiveStockSelect } from "@/components/data-driven-inputs/selects/live-stock-select/LiveStockSelect";
import { NumericTextInputNoScroll } from "@/components/forms/NumericTextInputNoScroll";
import { FieldCaption } from "@/components/forms/FieldCaption";

interface Props {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
  defaultFormState: PriceCalculatorFormData;
}

export const PriceCalculatorCargoGoodsDetails: React.FC<Props> = ({
  formData,
  onChangeFormData,
  defaultFormState,
}) => {
  return (
    <Box>
      <Heading variant={"h4"}>Cargo details</Heading>
      <Space num={SpaceValues.SIXTEEN} />
      <Row gap={GapValues.TWENTYFOUR}>
        <FieldCaption label={"HG = Class 1 or 7"} width={100}>
          <Box height={38} justifyContent={"center"}>
            <Switch
              value={formData.cargoHazardousGoods}
              onValueChange={(cargoHazGoods) =>
                onChangeFormData("cargoHazardousGoods", cargoHazGoods)
              }
            />
          </Box>
        </FieldCaption>
        <FieldCaption label={"HG weight"} width={80}>
          <NumericAgrVehicleWeightTextInput
            value={formData.cargoHazardousGoodsTotalWeight}
            onValueChange={(cargoHazWeight) => {
              onChangeFormData(
                "cargoHazardousGoodsTotalWeight",
                cargoHazWeight
              );
              const rows = formData.cargoHazardousGoodsAmountRows;
              if (Number(cargoHazWeight) > 0 && !(rows && rows >= 1)) {
                onChangeFormData("cargoHazardousGoodsAmountRows", 1);
              }
            }}
          />
        </FieldCaption>
        <FieldCaption label={"No of rows"} width={80}>
          <NumericTextInputNoScroll
            value={formData.cargoHazardousGoodsAmountRows?.toString()}
            onValueChange={(cargoHazRows) =>
              onChangeFormData(
                "cargoHazardousGoodsAmountRows",
                parseInt(cargoHazRows)
              )
            }
            min={0}
          />
        </FieldCaption>
        <Box justifyContent={"flex-end"} spacing={SpacingValues.EIGHT}>
          <CheckboxWithLabel
            label={"Qualified goods"}
            size={"standard"}
            value={formData.cargoHazardousGoodsQualified}
            onValueChange={(cargoHazQualified) =>
              onChangeFormData(
                "cargoHazardousGoodsQualified",
                cargoHazQualified
              )
            }
          />
        </Box>
      </Row>
      <Space num={SpaceValues.TWENTYFOUR} />
      <Row gap={GapValues.TWENTYFOUR}>
        <FieldCaption label={"Animals"} width={80}>
          <Box height={38} justifyContent={"center"}>
            <Switch
              value={formData.livestock}
              onValueChange={(livestock) => {
                onChangeFormData("livestock", livestock);
                if (!livestock) {
                  onChangeFormData(
                    "livestockType",
                    defaultFormState.livestockType
                  );
                }
              }}
            />
          </Box>
        </FieldCaption>
        <FieldCaption label={"Animal type"} width={145}>
          <LiveStockSelect
            value={formData.livestockType}
            onValueChange={(value) => onChangeFormData("livestockType", value)}
            menuPortalTarget={document.body}
            isDisabled={!formData.livestock}
          />
        </FieldCaption>
      </Row>
      <Space num={SpaceValues.TWENTYFOUR} />
      <FieldCaption label={"Customs classification"} width={200}>
        <CustomsClassificationSelect
          value={formData.customsClassificationCode}
          onValueChange={(value) => {
            onChangeFormData("customsClassificationCode", value);
          }}
        />
      </FieldCaption>
    </Box>
  );
};
